import React, { useState, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import s from './Page.module.css';
import { ENTIDADES, CAMPOS } from '../config/importadorCampos';

const box = {
  card: { border: '1px solid var(--border-color, #d8dee6)', borderRadius: 6, padding: '14px 16px', background: 'var(--bg-card, #fff)' },
  tabs: { display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border-color, #d8dee6)' },
  tab: { padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'none', borderBottom: '2px solid transparent', color: 'var(--text-secondary, #666)' },
  tabAtiva: { borderBottom: '2px solid var(--primary, #1565ff)', color: 'var(--primary, #1565ff)' },
  linha: { display: 'flex', gap: 12, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eef1f5', fontSize: 13 },
  colLabel: { flex: '0 0 300px', display: 'flex', alignItems: 'center', gap: 8 },
  colObrig: { flex: '0 0 90px', textAlign: 'center' },
  colSelect: { flex: 1 },
};

const PREVIEW_MAX = 50;

export default function ImportadorDados() {
  const [entidade, setEntidade] = useState('produtos');
  const [aba, setAba] = useState('propriedades');
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [colunas, setColunas] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [mapeamento, setMapeamento] = useState({});
  const [atualizarExistentes, setAtualizarExistentes] = useState(true);
  const [criarVinculos, setCriarVinculos] = useState(true);
  const [pararNoErro, setPararNoErro] = useState(true);
  const [mostrarAvancado, setMostrarAvancado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState(null);
  const inputRef = useRef(null);

  const campos = useMemo(() => CAMPOS[entidade] || [], [entidade]);

  const trocarEntidade = (novaEntidade) => {
    setEntidade(novaEntidade);
    setMapeamento({});
    setResultado(null);
    setErro('');
  };

  const autoMapear = useCallback((colunasDaPlanilha, listaCampos) => {
    const mapa = {};
    for (const c of listaCampos) {
      const achada = colunasDaPlanilha.find(col => col.trim().toLowerCase() === c.label.toLowerCase());
      if (achada) mapa[c.campo] = achada;
    }
    return mapa;
  }, []);

  const selecionarArquivo = async (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    setErro(''); setResultado(null); setEnviando(true);
    setNomeArquivo(arquivo.name);
    try {
      const form = new FormData();
      form.append('arquivo', arquivo);
      const { data } = await api.post('/importador/analisar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setColunas(data.colunas);
      setLinhas(data.linhas);
      setMapeamento(autoMapear(data.colunas, campos));
      setAba('planilha');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao ler a planilha.');
      setColunas([]); setLinhas([]);
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const alterarMapeamento = (campo, coluna) => setMapeamento(m => ({ ...m, [campo]: coluna || undefined }));

  const importar = async () => {
    const faltando = campos.filter(c => c.obrigatorio && !mapeamento[c.campo]);
    if (faltando.length) {
      setErro(`Mapeie os campos obrigatórios: ${faltando.map(c => c.label).join(', ')}.`);
      return;
    }
    setErro(''); setImportando(true); setResultado(null);
    try {
      const { data } = await api.post(`/importador/${entidade}/executar`, {
        colunas, linhas, mapeamento,
        atualizar_existentes: atualizarExistentes,
        criar_vinculos: criarVinculos,
        parar_no_erro: pararNoErro,
      });
      setResultado(data);
      setAba('planilha');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao importar.');
    } finally {
      setImportando(false);
    }
  };

  return (
    <div>
      <div className={s.topbar}>
        <h2 className={s.titulo}>Importar Dados</h2>
      </div>

      <div style={box.tabs}>
        <button style={{ ...box.tab, ...(aba === 'propriedades' ? box.tabAtiva : {}) }} onClick={() => setAba('propriedades')}>
          Propriedades da importação
        </button>
        <button style={{ ...box.tab, ...(aba === 'planilha' ? box.tabAtiva : {}) }} onClick={() => setAba('planilha')}>
          Planilha importada
        </button>
      </div>

      {erro && <p className={s.erro} style={{ marginBottom: 12 }}>{erro}</p>}

      {aba === 'propriedades' && (
        <div style={box.card}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 600 }}>
              O que importar
              <select className={s.select} value={entidade} onChange={e => trocarEntidade(e.target.value)}>
                {ENTIDADES.map(en => <option key={en.id} value={en.id}>{en.label}</option>)}
              </select>
            </label>
            <button type="button" className={s.btnSecundario} disabled={enviando} onClick={() => inputRef.current?.click()}>
              {enviando ? 'Lendo planilha…' : '📂 Selecionar arquivo Excel'}
            </button>
            <input ref={inputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={selecionarArquivo} />
            {nomeArquivo && <span style={{ fontSize: 12.5, color: '#666' }}>{nomeArquivo} — {linhas.length} linha(s)</span>}
          </div>

          <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            A primeira linha do arquivo Excel é utilizada como cabeçalho.
          </p>

          <div style={{ ...box.linha, fontWeight: 700, color: '#888', fontSize: 11, textTransform: 'uppercase' }}>
            <div style={box.colLabel}>Campos do sistema</div>
            <div style={box.colObrig}>Obrigatório</div>
            <div style={box.colSelect}>Coluna da planilha</div>
          </div>
          {campos.map(c => (
            <div key={c.campo} style={box.linha}>
              <div style={box.colLabel}>{c.label}</div>
              <div style={box.colObrig}><input type="checkbox" checked={!!c.obrigatorio} disabled readOnly /></div>
              <div style={box.colSelect}>
                <select className={s.select} style={{ width: '100%' }}
                  value={mapeamento[c.campo] || ''}
                  onChange={e => alterarMapeamento(c.campo, e.target.value)}>
                  <option value="">— não importar —</option>
                  {colunas.map(col => <option key={col} value={col}>{col}</option>)}
                </select>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 14 }}>
            <button type="button" className={s.btnSecundario} onClick={() => setMostrarAvancado(v => !v)}>
              ⚙️ Configurações avançadas
            </button>
            {mostrarAvancado && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                  <input type="checkbox" checked={atualizarExistentes} onChange={e => setAtualizarExistentes(e.target.checked)} />
                  Atualizar cadastro já existente (mesmo código de barras / CPF-CNPJ) em vez de duplicar
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                  <input type="checkbox" checked={criarVinculos} onChange={e => setCriarVinculos(e.target.checked)} />
                  Criar automaticamente fornecedor/cliente/marca/grupo/subgrupo que não existir ainda
                </label>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
              <input type="checkbox" checked={pararNoErro} onChange={e => setPararNoErro(e.target.checked)} />
              Não importar nenhum item em caso de erro
            </label>
            <button type="button" className={s.btnPrimario} disabled={!linhas.length || importando} onClick={importar}>
              {importando ? 'Importando…' : '⬇ Importar'}
            </button>
          </div>
        </div>
      )}

      {aba === 'planilha' && (
        <div style={box.card}>
          {!linhas.length && <p style={{ color: '#888', fontSize: 13 }}>Selecione um arquivo Excel na aba "Propriedades da importação".</p>}

          {linhas.length > 0 && !resultado && (
            <>
              <p style={{ fontSize: 12.5, color: '#666', marginBottom: 10 }}>
                Pré-visualização — {linhas.length} linha(s) lida(s){linhas.length > PREVIEW_MAX ? `, mostrando as ${PREVIEW_MAX} primeiras` : ''}.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      {colunas.map(col => <th key={col} style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{col}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.slice(0, PREVIEW_MAX).map((linha, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eef1f5' }}>
                        {linha.map((v, j) => <td key={j} style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{v instanceof Date ? v.toLocaleDateString('pt-BR') : String(v ?? '')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {resultado && (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 13 }}>
                <span>Total: <strong>{resultado.total}</strong></span>
                <span style={{ color: '#15803d' }}>Criados/OK: <strong>{resultado.sucesso}</strong></span>
                <span style={{ color: '#0369a1' }}>Atualizados: <strong>{resultado.atualizados}</strong></span>
                <span style={{ color: resultado.duplicadas ? '#92400e' : '#888' }}>Duplicadas na planilha: <strong>{resultado.duplicadas}</strong></span>
                <span style={{ color: resultado.erros ? '#b91c1c' : '#888' }}>Erros: <strong>{resultado.erros}</strong></span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', width: 70 }}>Linha</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', width: 60 }}>Status</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Mensagem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.detalhes.map(d => (
                      <tr key={d.linha} style={{ borderBottom: '1px solid #eef1f5' }}>
                        <td style={{ padding: '6px 8px' }}>{d.linha}</td>
                        <td style={{ padding: '6px 8px' }}>{d.status === 'ok' ? '✅' : d.status === 'duplicada' ? '⚠️' : '❌'}</td>
                        <td style={{ padding: '6px 8px', color: d.status === 'erro' ? '#b91c1c' : d.status === 'duplicada' ? '#92400e' : 'inherit' }}>{d.mensagem}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
